import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space } from 'antd';

import { queryClient } from '@/Root';

import {
  useDeleteGroup,
  useGroupsReduced,
} from '@/core/domain/operational/Groups/Groups.hooks';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import GroupReportModal from '@/components/Operational/Inventory/Groups/GroupReportModal';
import { getTableScrollX } from '@/utils/table';
import GroupsExtraFilters from '@/components/Operational/Inventory/Groups/GroupExtraFilters';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import Authorities from '@/types/Authority';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import GroupCreateModal from '@/components/Operational/Inventory/Groups/GroupCreateModal';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import {
  GroupReduced,
  ListGroupsReducedQuery,
} from '@/core/domain/operational/Groups/Groups.types';

const GroupsPage = ({ route }: PageProps) => {
  const [modalCreateGroupOpen, modalCreateGroupActions] = useDisclosure();

  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<ListGroupsReducedQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListGroupsReducedQuery>({
      active: 'boolean',
      search: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage } = useGroupsReduced(
    queryDebounce,
    hasPermissions([Authorities.REGISTER_GROUP_VIEW]),
  );

  usePersistSearchParams(queryDebounce);

  const groups = flatInfiniteData(data);

  const deleteMutation = useDeleteGroup();

  const [modalReportVisible, setReportVisible] = useState(false);

  function toggleReportModal() {
    setReportVisible(!modalReportVisible);
  }

  function handleRemoveClick(group: GroupReduced) {
    Modal.confirm({
      title: intl.formatMessage({ id: 'groups.remove.modal.title' }),
      content: `${intl.formatMessage({
        id: 'groups.remove.modal.content',
      })}`,
      onOk: async () => {
        try {
          // Fetch Service to remove group item.
          await deleteMutation.mutateAsync(group.id);

          await queryClient.refetchQueries('groups');
        } catch (error) {}
      },
      okText: intl.formatMessage({
        id: 'groups.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  }

  const columns: ColumnsType<GroupReduced> = [
    {
      title: <FormattedMessage id="groups.list.header.description" />,
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 300,
    },
    {
      title: <FormattedMessage id="groups.list.header.area" />,
      dataIndex: 'subAreaName',
      key: 'area',
      ellipsis: true,
      width: 300,
    },
    {
      title: <FormattedMessage id="groups.list.header.actions" />,
      key: 'actions',
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/operational/inventory/groups/${row.id}`}
            disabled={!hasPermissions([Authorities.REGISTER_GROUP_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={!hasPermissions([Authorities.REGISTER_GROUP_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        persistSearchParams={false}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={!hasPermissions([Authorities.REGISTER_GROUP_REPORT])}
            >
              <FormattedMessage id="commons.page.button.generateReport" />
            </Button>

            <Button
              block
              type="primary"
              disabled={!hasPermissions([Authorities.REGISTER_GROUP_ADD])}
              onClick={modalCreateGroupActions.open}
            >
              <FormattedMessage id="groups.searchBar.button.newGroup" />
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          searchValue={queryParams?.search}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          extraFilters={
            <GroupsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<GroupReduced>
          columns={columns}
          dataSource={groups}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <GroupReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />

        <GroupCreateModal
          onCancel={modalCreateGroupActions.close}
          open={modalCreateGroupOpen}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default GroupsPage;
